/* global $, DISABLED_DATES */

import pad from '../helpers/pad';

const $datepickers = $('.js-datepicker');

const DATEPICKER_LANG = {
  nl: {
    days: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
    daysShort: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
    months: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
    monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
  },
};

const datepickerObj = {
  /**
   * Setup things like languages and options and start initialising the datepickers
   * @return {void}
   */
  initialize() {
    if (!$datepickers.length) {
      return;
    }

    import(/* webpackChunkName: "datepicker" */ '@chenfengyuan/datepicker').then(() => {
      // Setup the language to use for the datepickers
      this.lang = DATEPICKER_LANG.nl;

      // 'Symlink' daysMin to short because they're the same and I don't
      // want to waste precious bytes
      this.lang.daysMin = this.lang.daysShort;

      this.baseOptions = {
        days: this.lang.days,
        daysShort: this.lang.daysShort,
        daysMin: this.lang.daysMin,
        months: this.lang.months,
        monthsShort: this.lang.monthsShort,
        autoHide: true,
        format: 'dd-mm-yyyy',
        disabledClass: 'disabled js-is-disabled',
      };

      this.getDisabledDates();

      // Initialize all datepickers
      $datepickers.toArray().forEach(this.createDatepicker.bind(this));
    });
  },

  getDisabledDates() {
    if (typeof DISABLED_DATES !== 'undefined') {
      // Change the format of disabled dates to a more easily to use format
      this.disabledDates = DISABLED_DATES.map((disabledDate) => ({
        date: disabledDate.date,
        reason: disabledDate.reason,
      }));
    }
  },

  /**
   * Create a datepicker
   * @param {HTMLInputElement} datepicker
   * @return {void}
   */
  createDatepicker(datepicker) {
    const $datepicker = $(datepicker);

    // Clone the options to prevent mutating the options object in other places
    const options = { ...this.baseOptions };

    // Set start date in options if defined on the input element
    if (typeof $datepicker.data('start-date') !== 'undefined') {
      options.startDate = $datepicker.data('start-date');
    }

    // Set disabled dates if defined on the input element
    if (typeof $datepicker.data('disable-dates') !== 'undefined') {
      options.filter = (date) => {
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        const toStr = this.toDatepickerString(day, month, year);
        return !this.checkDisabled(toStr);
      };
    }

    // Setupt disabled reasons handling if requested
    if (typeof $datepicker.data('disable-reasons') !== 'undefined') {
      options.show = this.addReasonListeners.bind(this);
      options.hide = this.removeReasonListeners.bind(this);
    }

    if (typeof $datepicker.data('start-date') !== 'undefined') {
      options.startDate = $datepicker.data('start-date');
    }

    // And last but not least -- create the datepicker!
    $datepicker.datepicker(options);
  },

  /**
   * Check if the given date is in the disabled dates array
   * @param {String} date
   * @return {Boolean}
   */
  checkDisabled(date) {
    return this.disabledDates.filter((obj) => obj.date === date).length;
  },

  /**
   * Get the message for the disabled date
   * @param {String} date
   * @return {String}
   */
  getDisabledMessage(date) {
    return this.disabledDates.filter((obj) => obj.date === date)[0].reason;
  },

  /**
   * Normalise the given day, month and year to a default format
   * @param {Number} day
   * @param {Number} month
   * @param {Number} year
   * @return {String}
   */
  toDatepickerString(day, month, year) {
    const dayPadded = pad(day, 2);
    const monthPadded = pad(month, 2);
    const yearPadded = year.length === 2 ? `20${year}` : year;

    return `${dayPadded}-${monthPadded}-${yearPadded}`;
  },

  /**
   * Get the datepicker dropdown element
   * @return {Object}
   */
  findDatepickerElement() {
    return $('.datepicker-container');
  },

  /**
   * Get the name of the month from the datepicker dropdown
   * @return {string}
   */
  getMonthName() {
    return this.$openDatepicker
      .find('[data-view="month current"]')
      .text()
      .replace(/\d{4}$/, '')
      .trim();
  },

  /**
   * Get the year from the datepicker dropdown
   * @return {number}
   */
  getYear() {
    const year = this.$openDatepicker
      .find('[data-view="month current"]')
      .text()
      .match(/(\d{4})$/, '');

    return parseInt(year[0], 10);
  },

  /**
   * Get the index of the month by name
   * @param {string} name
   * @return {number}
   */
  getMonthIndex(name) {
    return this.lang.monthsShort.indexOf(name) + 1;
  },

  /**
   * Get the date for the selected day
   * @param {HTMLElement} target
   * @return {string}
   */
  getDateForSelection(target) {
    const day = target.textContent.trim();
    const visibleMonthIndex = this.getMonthIndex(this.getMonthName());
    const isPrev = $(target).data('view') === 'day prev';
    const isNext = $(target).data('view') === 'day next';
    const year = this.getYear(this.$openDatepicker);

    let monthIndex = visibleMonthIndex;
    if (isPrev) { monthIndex -= 1; }
    if (isNext) { monthIndex += 1; }

    return this.toDatepickerString(day, monthIndex, year);
  },

  /**
   * Start listening on the datepicker dropdown for disabled dates hover
   * @return {void}
   */
  addReasonListeners() {
    this.$openDatepicker = this.findDatepickerElement();

    // Listen for mousenters on all day elements
    this.$openDatepicker.on('mouseenter', '[data-view^="day"]', (e) => {
      const date = this.getDateForSelection(e.target);
      const isDisabled = this.checkDisabled(date);

      if (isDisabled) {
        const message = this.getDisabledMessage(date);
        this.outputMessage(message);
      } else {
        this.outputMessage('');
      }
    });
  },

  /**
   * Remove listening on the datepicker dropdown for disabled dates hover
   * @return {void}
   */
  removeReasonListeners() {
    this.$openDatepicker.off('mouseenter', '[data-view^="day"]');
  },

  /**
   * Output the disabled date reason to the datepicker
   * @param {string} text
   * @return {void}
   */
  outputMessage(text) {
    const $error = this.$openDatepicker.find('.picker-error');
    $error.remove();

    if (text === '') {
      return true;
    }

    this.$openDatepicker.append(`
      <div class="picker-error">
        ${text}
      </div>
    `);

    return true;
  },
};

export default datepickerObj;
